import React from 'react';
import { Box, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import {
  COLORS_GREEN,
  COLORS_GREEN20,
  COLORS_GREEN30,
  COLORS_GREEN40,
  COLORS_LIGHTGRAYBG,
  COLORS_WHITE,
} from '../../App.constants';
import { useLocalStore } from '../local-store-provider/LocalStoreProvider.utils';
import { RevenueCalculatorStore } from '../revenue-calculator/RevenueCalculator.store';
import { observer } from 'mobx-react';
import { SwapHoriz } from '@mui/icons-material';
import { CallToAction } from './call-to-action/CallToAction';
import { CustomTableCell } from '../calculator-totals/custom-table-cell/CustomTableCell';
import { toCurrency } from '../../utils/currency/currency.utils';
import { CurrencyCode } from '../../utils/currency/currency.constants';

export const BenchmarksMobile: React.FC = observer(() => {
  const localStore = useLocalStore<RevenueCalculatorStore>();

  return (
    <Box
      display={'flex'}
      width={'100%'}
      alignItems={'center'}
      flexDirection={'column'}
      bgcolor={COLORS_LIGHTGRAYBG}
      paddingTop={4}
      marginBottom={4}
      position={'relative'}
    >
      <Typography
        color={COLORS_GREEN}
        fontWeight={'bold'}
        fontSize={'1.563em'}
        textAlign={'center'}
        marginBottom={2}
      >
        <Box
          width={'40px'}
          height={'40px'}
          display={'inline'}
          marginRight={'8px'}
          marginLeft={'-2px'}
        >
          <SwapHoriz
            style={{ marginBottom: '-10px', marginRight: '-5px', width: '40px', height: '40px', color: COLORS_GREEN }}
          />
        </Box>
        How do you stack up to industry benchmarks?
      </Typography>

      {!localStore.hasSubmittedEmail && (
        <Typography textAlign={'center'}>
          Enter your email address below to see how you stack up to industry benchmarks for revenue & utilization.
        </Typography>
      )}

      <Box position={'relative'}>
        {!localStore.hasSubmittedEmail && <CallToAction />}

        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: COLORS_WHITE }}>
              <CustomTableCell></CustomTableCell>
              <CustomTableCell
                isCenter={true}
                align="left"
                sx={{ fontWeight: 600, lineHeight: '1.2rem' }}
              >
                Revenue per Billable Employee
              </CustomTableCell>
              <CustomTableCell
                sx={{ fontWeight: 600, lineHeight: '1.2rem' }}
                align="left"
              >
                Revenue per Employee
              </CustomTableCell>
              <CustomTableCell
                isCenter={true}
                sx={{ fontWeight: 600, lineHeight: '1.2rem' }}
                align="left"
              >
                Target Average Utilization
              </CustomTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow sx={{ backgroundColor: COLORS_LIGHTGRAYBG }}>
              <CustomTableCell>
                <b>Your Results</b>
              </CustomTableCell>
              <CustomTableCell
                isCenter={true}
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(localStore.revenuePerBillableEmployee, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(localStore.revenuePerEmployee, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="left"
                isCenter={true}
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {localStore.targetAverageUtilization}%
              </CustomTableCell>
            </TableRow>

            <TableRow sx={{ backgroundColor: COLORS_GREEN20 }}>
              <CustomTableCell>
                <b>Low</b>
              </CustomTableCell>
              <CustomTableCell
                isCenter={true}
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(104000, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(77000, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="left"
                isCenter={true}
                shouldHide={!localStore.hasSubmittedEmail}
              >
                60%
              </CustomTableCell>
            </TableRow>

            <TableRow sx={{ backgroundColor: COLORS_GREEN30 }}>
              <CustomTableCell>
                <b>Mid</b>
              </CustomTableCell>
              <CustomTableCell
                isCenter={true}
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(215000, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(175000, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="left"
                isCenter={true}
                shouldHide={!localStore.hasSubmittedEmail}
              >
                75%
              </CustomTableCell>
            </TableRow>

            <TableRow sx={{ backgroundColor: COLORS_GREEN40 }}>
              <CustomTableCell>
                <b>High</b>
              </CustomTableCell>
              <CustomTableCell
                isCenter={true}
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(2910000, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="right"
                shouldHide={!localStore.hasSubmittedEmail}
              >
                {toCurrency(253000, {
                  currencyCode: CurrencyCode.USD,
                  fractionDigits: 0,
                })}
              </CustomTableCell>
              <CustomTableCell
                align="left"
                isCenter={true}
                shouldHide={!localStore.hasSubmittedEmail}
              >
                85%
              </CustomTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
});

BenchmarksMobile.displayName = 'BenchmarksMobile';
